import { AmericanoPlayer, AmericanoMatch } from './types';

// Choose a playing roster of size multiple-of-4 and a bench from remaining players
export const computeAmericanoRoster = (players: AmericanoPlayer[]) => {
  const total = players.length;
  // No court limit - support unlimited players with automatic benching
  const courts = Math.max(1, Math.floor(total / 4));
  const playingCount = courts * 4;

  // Sort by games played ascending to rotate bench fairly; tie-break by name
  const sorted = [...players].sort((a, b) => {
    if (a.gamesPlayed !== b.gamesPlayed) return a.gamesPlayed - b.gamesPlayed;
    return a.name.localeCompare(b.name);
  });

  const playing = sorted.slice(0, playingCount);
  const bench = sorted.slice(playingCount);

  // Mark bench flags (do not mutate input)
  const updated = players.map(p => ({
    ...p,
    isOnBench: bench.some(b => b.id === p.id),
  }));

  return { playing, bench, updated };
};

// Corrected round-robin partnership generation
export const generateAmericanoPartnerships = (playerList: AmericanoPlayer[], round: number) => {
  const players = [...playerList];
  const partnerships: Array<{ player1: AmericanoPlayer; player2: AmericanoPlayer }> = [];
  const playerCount = players.length;

  if (playerCount % 2 !== 0) {
    console.error('Partnership generation failed: odd number of players', {
      playerCount,
      playerNames: players.map(p => p.name)
    });
    throw new Error(`Cannot generate partnerships: ${playerCount} players (must be even). Check roster computation.`);
  }

  // Rotate players for each round to generate new partnerships
  const rotation = round - 1;
  const rotatedPlayers = [...players];
  
  if (rotation > 0) {
    // Keep the first player fixed and rotate the rest
    const toRotate = rotatedPlayers.splice(1);
    for (let i = 0; i < rotation; i++) {
      toRotate.push(toRotate.shift()!);
    }
    rotatedPlayers.push(...toRotate);
  }

  // Pair players
  const half = playerCount / 2;
  for (let i = 0; i < half; i++) {
    partnerships.push({
      player1: rotatedPlayers[i],
      player2: rotatedPlayers[playerCount - 1 - i]
    });
  }
  
  console.log(`Generated ${partnerships.length} partnerships for ${playerCount} players in round ${round}`);
  return partnerships;
};

// Improved court assignment based on combined rankings
export const assignAmericanoCourts = (partnerships: Array<{ player1: AmericanoPlayer; player2: AmericanoPlayer }>) => {
  const rankedPartnerships = partnerships
    .map(pair => ({
      ...pair,
      combinedPoints: pair.player1.points + pair.player2.points,
      averagePoints: (pair.player1.points + pair.player2.points) / 2
    }))
    .sort((a, b) => b.combinedPoints - a.combinedPoints);

  const newMatches: AmericanoMatch[] = [];
  
  // For an even number of partnerships, we need half that number of courts
  // Each match has 2 teams (4 players total)
  for (let i = 0; i < rankedPartnerships.length; i += 2) {
    if (rankedPartnerships[i + 1]) {
      newMatches.push({
        court: Math.floor(i / 2) + 1,  // Court numbers start at 1
        team1: {
          player1: rankedPartnerships[i].player1,
          player2: rankedPartnerships[i].player2
        },
        team2: {
          player1: rankedPartnerships[i + 1].player1,
          player2: rankedPartnerships[i + 1].player2
        }
      });
    } else {
      // This should never happen in a properly set up Americano tournament
      // with an even number of players, but handle it just in case
      console.warn("Odd number of partnerships detected. This shouldn't happen in Americano.");
    }
  }
  
  console.log(`Created ${newMatches.length} matches from ${rankedPartnerships.length} partnerships`);
  return newMatches;
};

// Generate matches for Team Americano using fixed teams but rotating opponents
export const generateTeamAmericanoMatches = (
  fixedTeams: Array<{ player1: AmericanoPlayer; player2: AmericanoPlayer }>,
  round: number
): Array<{ player1: AmericanoPlayer; player2: AmericanoPlayer }> => {
  
  const teamCount = fixedTeams.length;
  if (teamCount < 2) {
    throw new Error('Need at least 2 teams for Team Americano');
  }

  // Use round-robin algorithm to rotate TEAMS (not players)
  const rotation = round - 1;
  const rotatedTeams = [...fixedTeams];
  
  if (rotation > 0 && teamCount > 2) {
    // Keep the first team fixed and rotate the rest (standard round-robin)
    const toRotate = rotatedTeams.splice(1);
    for (let i = 0; i < rotation; i++) {
      toRotate.push(toRotate.shift()!);
    }
    rotatedTeams.splice(1, 0, ...toRotate);
  }

  // Return the rotated teams as partnerships (they're already teams)
  console.log(`Generated Team Americano matchups for round ${round}:`, 
    rotatedTeams.map(t => `${t.player1.name} & ${t.player2.name}`));
  
  return rotatedTeams;
};
